<template>
  <div class="message-box dialog-mask" @click.self="onClickOutSide">
    <div class="dialog-content">
      <header>{{ title }}</header>
      <div class="dialog-body">
        <span class="message">{{ content }}</span>
        <input-text
          class="input attribute"
          :label="placeholder"
          :value="value"
          @input="(val) => (value = val)"
          :maxlength="limit"
        ></input-text>
        <span v-if="error.error" class="error">{{ error.message }}</span>
      </div>
      <footer>
        <div class="button-area">
          <button class="button cancel" @click="onNegativeClick">{{ negative }}</button>
          <button class="button save" @click="onPositiveClick">{{ positive }}</button>
        </div>
      </footer>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import InputText from "@/components/atoms/InputText.vue";

type InputError = {
  error: boolean;
  message: string;
};

interface Data {
  error: InputError;
  value: string;
}

interface Methods {
  onClickOutSide: () => void;
  onNegativeClick: () => void;
  onPositiveClick: () => void;
}

interface Props {
  title: string;
  content: string;
  input: string;
  placeholder?: string;
  limit?: number;
  negative?: string;
  positive?: string;
  negativeCallback?: () => void;
  positiveCallback?: (value: string) => void;
}

export default Vue.extend<Data, Methods, {}, Props>({
  components: { InputText },
  data() {
    return {
      error: {
        error: false,
        message: "",
      },
      value: this.input,
    };
  },
  methods: {
    onNegativeClick() {
      if (this.negativeCallback) {
        this.negativeCallback();
      }

      this.$close(true);
    },
    onPositiveClick() {
      const { value } = this;

      if (value.length < 1) {
        this.error = {
          error: true,
          message: this.$t('materialPage.sideMenuExpandEditableLink.attributeNameEmptyError') as string,
        };
        return;
      }

      if (this.positiveCallback) {
        this.positiveCallback(value);
      }

      this.$close(true);
    },
    onClickOutSide() {
      this.$close(true);
    },
  },
  props: {
    title: String,
    content: String,
    input: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    limit: {
      type: Number,
      default: 20,
    },
    negative: {
      type: String,
      default() {
        return this.$t('common.cancel');
      },
    },
    negativeCallback: Function,
    positive: {
      type: String,
      default() {
        return this.$t('common.create');
      },
    },
    positiveCallback: Function,
  },
});

export type MaterialAttributeEditDialogProps = Props;
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

header {
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-xl);
  margin: 15px auto;
  margin-top: 0px;
  text-align: center;
  color: var(--color-text-normal);
}

.button-area {
  width: 100%;
  margin: 10px auto;
  margin-bottom: 0px;
  float: right;
  display: flex;
  flex-direction: row;
}

.fade-enter-active {
  transition: opacity 0.3s;
}

.fade-leave-active {
  transition: opacity 0.1s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.dialog-body {
  font-size: var(--font-size-md);
  display: flex;
  flex-direction: column;
  align-items: center;
  white-space: pre-wrap;
}

.message {
  text-align: center;
  padding-bottom: 1rem;
}

.dialog-mask {
  position: fixed;
  top: -100px;
  right: 0;
  bottom: 0;
  left: 0;
  display: -ms-flexbox;
  display: flex;
  z-index: 9000;
  -ms-flex-align: center;
  align-items: center;
  -ms-flex-pack: center;
  justify-content: center;
  background-color: var(--color-overlay);
}

.dialog-content {
  margin: 0 10px;
  padding: 1.5rem 3rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background-color: var(--color-background-normal);
  box-shadow: var(--shadow-md);
  color: var(--color-text-normal);
}

.button {
  position: relative;
  @include hover-overlay;
  flex: 1;
  color: var(--color-text-on-fixed-bg);
  border: none;
  padding: 10px 20px;
  border-radius: var(--radius-xs);
  cursor: pointer;
  font-weight: var(--font-weight-bold);
  margin: 0 5px;
  transition: filter 0.2s ease;


  &.save {
    background-color: var(--color-primary);
  }

  &.cancel {
    position: relative;
    @include hover-overlay;
    background-color: var(--color-surface-muted);
    color: var(--color-text-normal);
    border: 1px solid var(--color-border);
  }
}

.input {
  font-size: var(--font-size-sm);
  padding: 0.5rem;

  &.attribute {
    width: 95%;
  }
}

.error {
  width: 100%;
  text-align: start;
  font-size: var(--font-size-xs);
  margin-top: 0.5rem;
  color: var(--color-warning);
}
</style>
